import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { useOrgStore } from '@/stores/orgStore';
import { pickHighestRole, type Role, type OrgRole } from '@/types/roles';

export function useRole() {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const authLoading = useAuthStore((s) => s.isLoading);
  const orgRole = useOrgStore((s) => s.memberRole);
  const orgLoading = useOrgStore((s) => s.isLoading);

  // Check if user is a platform admin
  const adminCheck = useQuery({
    queryKey: ['is-platform-admin', user?.id],
    enabled: isAuthenticated && !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('is_platform_admin');
      if (error) throw error;
      return data === true;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const isAdmin = adminCheck.data ?? false;
  const resolvedRole: Role | null = isAdmin ? 'ADMIN' : orgRole;
  const isLoading = authLoading || orgLoading || adminCheck.isLoading;

  return {
    role: resolvedRole,
    isAdmin,
    isLoading,
    isAuthenticated,
    user,
  };
}

/** Resolve the highest org role from an array of memberships */
export function useResolvedRole(roles: OrgRole[]): OrgRole | null {
  return pickHighestRole(roles);
}
